/**
 * HotX 域名套餐入口与删除倒计时状态模型。
 * 版本：Console 0.1.101；更新时间：2026-09-15 14:51 CST；精确父基线：100f5b30716e211528102566acce5db7efc794ea。
 * 添加域名不混入免费套餐；后台推送套餐即使到期也持续锁定，只有人工取消才退出。
 */
// CDN-GUEST-PACKAGE-PREVIEW-20260915: 游客只使用官网公开预览事实；登录后必须映射为实时套餐，预览键不得提交。
import type { ActivePushPlan, DomainPlanOverview } from '../../api/pointApi';

export type DomainPlanEntryMode = 'PAID' | 'FREE';
export type DomainPlanEntryKey = 'free' | 'developer' | 'standard' | 'business';

export interface DomainPlanChoice {
  key: string;
  kind: 'FREE' | 'PAID' | 'ADMIN_PUSH';
  label: string;
  pointPrice: number;
  locked: boolean;
  supportsAutoRenew: boolean;
  planId?: number;
  planCode?: string;
}

export interface GuestPaidPlanPreview {
  entryKey: Exclude<DomainPlanEntryKey, 'free'>;
  key: `PREVIEW:${Exclude<DomainPlanEntryKey, 'free'>}`;
  label: string;
  planCode: string;
  pointPrice: number;
  quotaLabel: string;
  postSubmitQuotaLabel: string;
}

const GUEST_PAID_PLAN_PREVIEWS: GuestPaidPlanPreview[] = [
  {
    entryKey: 'developer',
    key: 'PREVIEW:developer',
    label: '开发版',
    planCode: 'DEVELOPER',
    pointPrice: 29,
    quotaLabel: '单域名0/1',
    postSubmitQuotaLabel: '单域名1/1'
  },
  {
    entryKey: 'standard',
    key: 'PREVIEW:standard',
    label: '标准版',
    planCode: 'STANDARD',
    pointPrice: 99,
    quotaLabel: '单域名0/1',
    postSubmitQuotaLabel: '单域名1/1'
  },
  {
    entryKey: 'business',
    key: 'PREVIEW:business',
    label: '商业版',
    planCode: 'BUSINESS',
    pointPrice: 999,
    quotaLabel: '根域名0/2',
    postSubmitQuotaLabel: '根域名1/2'
  }
];

export function buildGuestPaidPlanPreviews(): GuestPaidPlanPreview[] {
  return GUEST_PAID_PLAN_PREVIEWS.map(preview => ({ ...preview }));
}

export function parseGuestPaidPlanPreviewKey(value: unknown): GuestPaidPlanPreview['entryKey'] | null {
  if (typeof value !== 'string' || !value.startsWith('PREVIEW:')) return null;
  const entryKey = parseDomainPlanEntryKey(value.slice('PREVIEW:'.length));
  return entryKey && entryKey !== 'free' ? entryKey : null;
}

const ENTRY_PLAN_CODE_ALIASES: Record<DomainPlanEntryKey, string[]> = {
  free: ['FREE', 'FREE_CDN'],
  developer: ['DEVELOPER', 'PURCHASE_CDN'],
  standard: ['STANDARD'],
  business: ['BUSINESS']
};

export function parseDomainPlanEntryKey(value: unknown): DomainPlanEntryKey | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized === 'free'
    || normalized === 'developer'
    || normalized === 'standard'
    || normalized === 'business'
    ? normalized
    : null;
}

export function resolveEntryPlanChoice(
  choices: DomainPlanChoice[],
  entryKey: DomainPlanEntryKey | null | undefined
): DomainPlanChoice | null {
  if (!entryKey) return null;
  if (entryKey === 'free') return choices.find(choice => choice.kind === 'FREE') || null;
  const aliases = ENTRY_PLAN_CODE_ALIASES[entryKey];
  return choices.find(choice =>
    choice.kind === 'PAID'
    && aliases.includes(String(choice.planCode || '').trim().toUpperCase())
  ) || null;
}

function displayPoints(value: number) {
  return Number.isInteger(value) ? String(value) : String(value).replace(/0+$/, '').replace(/\.$/, '');
}

function pushedChoice(push: ActivePushPlan): DomainPlanChoice {
  return {
    key: `PUSH:${push.assignmentId}`,
    kind: 'ADMIN_PUSH',
    label: `${push.planName} · 后台推送`,
    pointPrice: 0,
    locked: true,
    supportsAutoRenew: false,
    planId: push.planId
  };
}

export function buildDomainPlanChoices(
  overview: DomainPlanOverview | null | undefined,
  entryMode: DomainPlanEntryMode
): DomainPlanChoice[] {
  if (overview?.activePush) return [pushedChoice(overview.activePush)];

  const paid = (overview?.plans || []).map(plan => ({
    key: `PAID:${plan.id}`,
    kind: 'PAID' as const,
    label: `${plan.planName} · ${displayPoints(plan.pointPrice)} 积分/月`,
    pointPrice: Number(plan.pointPrice),
    locked: false,
    supportsAutoRenew: true,
    planId: plan.id,
    planCode: plan.planCode
  }));

  if (entryMode === 'PAID') return paid;
  return [{
    key: 'FREE',
    kind: 'FREE',
    label: '免费 CDN 套餐',
    pointPrice: Number(overview?.dailyPointPrice || 1),
    locked: false,
    supportsAutoRenew: true,
    planCode: 'FREE'
  }, ...paid];
}

export function resolveInitialPlanSelection(
  overview: DomainPlanOverview | null | undefined,
  entryMode: DomainPlanEntryMode
): DomainPlanChoice | null {
  const choices = buildDomainPlanChoices(overview, entryMode);
  // V1.1 (2026-09-10): every entry defaults to its first allowed choice.
  // PAID never exposes FREE; an active push still replaces the complete catalog.
  return choices[0] || null;
}

export function describePushExpiry(
  push: ActivePushPlan,
  serverTime: string
): string {
  if (!push.expireAt) return '长期有效';
  const expiresAt = Date.parse(push.expireAt);
  const now = Date.parse(serverTime);
  if (!Number.isFinite(expiresAt) || !Number.isFinite(now)) return '到期时间无法判断';
  if (now < expiresAt) {
    const days = Math.max(1, Math.ceil((expiresAt - now) / 86_400_000));
    return `${days} 天后到期`;
  }
  const days = Math.max(0, Math.floor((now - expiresAt) / 86_400_000));
  return `已过期 ${days} 天`;
}

export function formatDeletionCountdown(
  deleteAt: string | null | undefined,
  serverNow: string | null | undefined
): string {
  if (!deleteAt || !serverNow) return '';
  const deadline = Date.parse(deleteAt);
  const now = Date.parse(serverNow);
  if (!Number.isFinite(deadline) || !Number.isFinite(now)) return '';
  const remainingMs = deadline - now;
  if (remainingMs <= 0) return '已到删除时间，等待系统处理';
  if (remainingMs < 3_600_000) return '保护期剩余不足 1 小时';
  if (remainingMs < 86_400_000) return `保护期剩余 ${Math.ceil(remainingMs / 3_600_000)} 小时`;
  return `保护期剩余 ${Math.ceil(remainingMs / 86_400_000)} 天`;
}
